/* eslint-disable @typescript-eslint/no-explicit-any */
/* Format painter: copy the LOOK of one visual and paste it onto others.
 *
 * What counts as "format" (everything lives under widget.display):
 *
 *  UNIVERSAL -- pasted onto any visual type:
 *    showTitle, showBorder, showLastRefresh, alignment, titleStyle, cardStyle
 *
 *  GROUP -- pasted only onto visuals of the same catalog group (e.g. column ->
 *  line chart, table -> matrix) or the same type:
 *    numberFormat, format, chart (see CHART_DATA_KEYS), tablePresentation
 *    (header/body styles, overflow; column widths stay), kpi
 *
 *  NEVER copied (they describe THIS visual's data or content, not its look):
 *    widget.title / text / html / css, datasetId, query (fields, filters, sort,
 *    limit), interaction, layout, id, conditionalFormatting (bound to field
 *    names), matrix (row/column field choices), valueTable, textSearch,
 *    display.chart.{stackMode, orientation, seriesAxis, categoryColors,
 *    seriesColorMap, referenceLines} (chart TYPE / per-series / per-category /
 *    per-value settings), tablePresentation.columnWidths (per column name).
 *
 * Paste overwrites: if the source has no value for a copied key, the target's
 * value is removed so both end up looking the same. */
import { specFor } from "./catalog";
import type { SPage, SWidget } from "./model";

export const UNIVERSAL_KEYS = ["showTitle", "showBorder", "showLastRefresh", "alignment", "titleStyle", "cardStyle"] as const;
export const GROUP_KEYS = ["numberFormat", "format", "chart", "tablePresentation", "kpi"] as const;
/** Chart settings that are about data / chart kind, not look. */
export const CHART_DATA_KEYS = ["stackMode", "orientation", "seriesAxis", "categoryColors", "seriesColorMap", "referenceLines"] as const;
/** tablePresentation settings that are per column. */
export const TABLE_DATA_KEYS = ["columnWidths"] as const;

const DATA_SUBKEYS: Record<string, readonly string[]> = { chart: CHART_DATA_KEYS, tablePresentation: TABLE_DATA_KEYS };

export interface FormatClip {
  type: string;
  group: string;
  /** Copied display values, deep-cloned (so later edits of the source do not leak in). */
  values: Record<string, any>;
  /** For the toast / button tooltip. */
  sourceTitle: string;
}

const isObj = (v: unknown): v is Record<string, any> => v !== null && typeof v === "object" && !Array.isArray(v);

/** Visuals of the same group (or the same type) can exchange group-specific format. */
export function groupOf(type: string): string {
  return specFor(type)?.group ?? `type:${type}`;
}
export function sameKind(a: string, b: string): boolean {
  return a === b || groupOf(a) === groupOf(b);
}

function stripped(key: string, value: any): any {
  const drop = DATA_SUBKEYS[key];
  if (!drop || !isObj(value)) return value;
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(value)) if (!drop.includes(k)) out[k] = v;
  return out;
}

export function copyFormat(w: SWidget): FormatClip {
  const d = (w.display ?? {}) as Record<string, any>;
  const values: Record<string, any> = {};
  for (const k of [...UNIVERSAL_KEYS, ...GROUP_KEYS]) if (d[k] !== undefined) values[k] = structuredClone(stripped(k, d[k]));
  return { type: w.type, group: groupOf(w.type), values, sourceTitle: w.title || specFor(w.type)?.label || w.type };
}

/** Paste onto one widget. Returns the same object when nothing would change. */
export function pasteFormatOn(w: SWidget, clip: FormatClip): SWidget {
  const sameKindAsSource = sameKind(clip.type, w.type);
  const keys: string[] = [...UNIVERSAL_KEYS, ...(sameKindAsSource ? GROUP_KEYS : [])];
  const before = (w.display ?? {}) as Record<string, any>;
  const display: Record<string, any> = { ...before };
  for (const k of keys) {
    const incoming = clip.values[k];
    const keep = DATA_SUBKEYS[k] && isObj(before[k]) ? Object.fromEntries(Object.entries(before[k]).filter(([kk]) => DATA_SUBKEYS[k].includes(kk))) : {};
    if (incoming === undefined) {
      if (Object.keys(keep).length) display[k] = keep;
      else delete display[k];
    } else if (DATA_SUBKEYS[k] && isObj(incoming)) display[k] = { ...structuredClone(incoming), ...keep };
    else display[k] = structuredClone(incoming);
  }
  return JSON.stringify(display) === JSON.stringify(before) ? w : { ...w, display };
}

export interface PasteResult {
  page: SPage;
  changed: number;
  /** Targets that were already identical. */
  same: number;
  /** Targets that only received the universal keys because their type differs. */
  partial: number;
}

export function pasteFormat(page: SPage, ids: string[], clip: FormatClip): PasteResult {
  let changed = 0;
  let same = 0;
  let partial = 0;
  const widgets = page.widgets.map((w) => {
    if (!ids.includes(w.id)) return w;
    const next = pasteFormatOn(w, clip);
    if (next === w) same += 1;
    else {
      changed += 1;
      if (!sameKind(clip.type, w.type)) partial += 1;
    }
    return next;
  });
  return { page: changed ? { ...page, widgets } : page, changed, same, partial };
}
